import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Search, Loader2, PenLine, Send, Trash2, TrendingUp, Users, Repeat, UserPlus, FileText, ChevronDown, ChevronUp, Save, Share2, Copy, Mail, Plus } from "lucide-react";

const OPP_TINT = {
  gap: "bg-emerald-500/15 text-emerald-300",
  close_to_ranking: "bg-amber-500/15 text-amber-300",
  update: "bg-blue-500/15 text-blue-300",
  internal_link: "bg-purple-500/15 text-purple-300",
  ai_search: "bg-teal-500/15 text-teal-300",
};

export const SeoEngine = () => {
  const [status, setStatus] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [articles, setArticles] = useState([]);
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [batching, setBatching] = useState(false);
  const [growth, setGrowth] = useState(null);

  const load = async () => {
    const [f, k, a, g] = await Promise.all([api.seoFunnel(), api.seoKeywords(), api.seoArticles(), api.seoGrowth(100000)]);
    setFunnel(f); setKeywords(k); setArticles(a); setGrowth(g);
  };
  useEffect(() => { api.seoStatus().then(setStatus).catch(() => {}); load(); }, []);

  const batchWrite = async () => {
    if (!keywords.length) return;
    setBatching(true);
    try {
      const r = await api.seoBatchWrite(keywords.map((k) => k.keyword));
      toast.success(`KURT is batch-writing ${r.queued} articles — they'll appear below.`);
      let n = 0;
      const t = setInterval(async () => { n++; await load(); if (n > 30) { clearInterval(t); setBatching(false); } }, 6000);
    } catch { toast.error("Batch write failed."); setBatching(false); }
  };

  const discover = async () => {
    setBusy(true);
    try { const k = await api.seoDiscover(brief.trim()); setKeywords(k); toast.success(`Found ${k.length} search opportunities.`); await load(); }
    catch { toast.error("Discovery failed."); }
    finally { setBusy(false); }
  };

  const write = async (kw) => {
    toast.message(`KURT is writing "${kw.suggested_title || kw.keyword}"…`);
    try { await api.seoWrite(kw.keyword, kw.suggested_title || ""); await load(); toast.success("Article drafted."); }
    catch { toast.error("Couldn't write the article."); }
  };

  const f = funnel;
  return (
    <div data-testid="screen-seo" className="max-w-6xl mx-auto">
      <div className="kz-fade-up">
        <p className="text-sm text-emerald-400 font-semibold tracking-wide">SEO / READER ENGINE</p>
        <h1 className="kz-head text-3xl md:text-4xl font-extrabold tracking-tight mt-2">Search → Reader → Repeat → Member</h1>
        <p className="text-slate-400 mt-2 text-sm">KURT finds search & AI-answer opportunities, writes rankable editorial in your voice, and turns readers into members. {status && !status.analytics_live && <span className="text-amber-400">Demo mode — connect Search Console + your CMS to go live.</span>}</p>
      </div>

      {f && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6">
          <Stat icon={Search} label="Searchers" value={f.searchers?.toLocaleString()} accent="text-slate-200" testid="f-search" />
          <Stat icon={FileText} label="Readers" value={f.readers?.toLocaleString()} accent="text-emerald-400" sub={`${f.search_to_reader}% of searchers`} testid="f-readers" />
          <Stat icon={Repeat} label="Repeat readers" value={f.repeat_readers?.toLocaleString()} accent="text-blue-400" testid="f-repeat" />
          <Stat icon={UserPlus} label="Members" value={f.members?.toLocaleString()} accent="text-emerald-400" sub={`${f.reader_to_member}% of readers`} testid="f-members" />
          <Stat icon={TrendingUp} label="Articles" value={f.articles_total} accent="text-slate-200" sub={`${f.articles_published} live`} testid="f-articles" />
        </div>
      )}
      {f?.note && <p className="text-[11px] text-slate-500 mt-2">{f.note}</p>}

      {growth && (
        <div data-testid="growth-dashboard" className="mt-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.05] p-5 kz-fade-up">
          <div className="flex items-center gap-2 mb-3"><TrendingUp size={16} className="text-emerald-400" /><span className="text-xs font-bold uppercase tracking-wider text-emerald-400">Path to {growth.target.toLocaleString()} readers</span></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniG label="Current readers" value={growth.current_readers.toLocaleString()} />
            <MiniG label="Published articles" value={growth.articles_published} />
            <MiniG label="Weekly reader growth" value={`+${growth.weekly_reader_growth.toLocaleString()}`} />
            <MiniG label="ETA to target" value={growth.months_to_target > 0 ? `${growth.months_to_target} mo` : "—"} />
          </div>
          <p className="text-[11px] text-slate-500 mt-3">{growth.note}</p>
        </div>
      )}

      {/* Discover */}
      <div className="flex flex-col sm:flex-row gap-2 mt-5">
        <input data-testid="seo-brief-input" value={brief} onChange={(e) => setBrief(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !busy && discover()}
          placeholder="What to rank for (e.g. 'help for new UK small businesses')…"
          className="flex-1 bg-[#121824] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500/40" />
        <button data-testid="seo-discover-btn" onClick={discover} disabled={busy}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-[#04140d] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60 transition-all">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Find opportunities
        </button>
      </div>

      {/* Keyword opportunities */}
      {keywords.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">{keywords.length} opportunities</p>
            <button data-testid="batch-write-btn" onClick={batchWrite} disabled={batching}
              className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-500 text-[#04140d] hover:bg-emerald-400 disabled:opacity-60 transition-all">
              {batching ? <Loader2 size={13} className="animate-spin" /> : <PenLine size={13} />} Write all ({keywords.length})
            </button>
          </div>
          {keywords.map((k) => (
            <div key={k.id} data-testid={`kw-${k.id}`} className="rounded-xl border border-white/10 bg-[#121824] p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-100 truncate">{k.suggested_title || k.keyword}</span>
                  <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${OPP_TINT[k.opportunity_type] || "bg-white/5 text-slate-400"}`}>{(k.opportunity_type || "").replace(/_/g, " ")}</span>
                  {k.current_position > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400">pos {k.current_position}</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate">"{k.keyword}" · ~{(k.monthly_searches || 0).toLocaleString()}/mo · diff {k.difficulty} · {k.funnel_stage}</p>
              </div>
              <button data-testid={`write-${k.id}`} onClick={() => write(k)} className="shrink-0 flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-white/[0.06] text-slate-100 hover:bg-white/[0.1] transition-all"><PenLine size={13} className="text-emerald-400" /> Write</button>
            </div>
          ))}
        </div>
      )}

      {/* Articles */}
      <h2 className="kz-head text-lg font-bold mt-8 mb-3 flex items-center gap-2"><FileText size={16} className="text-emerald-400" /> Articles</h2>
      {articles.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#121824] p-8 text-center text-sm text-slate-400">No articles yet — find opportunities and let KURT write.</div>
      ) : (
        <div className="space-y-2">{articles.map((a) => <ArticleRow key={a.id} a={a} onChange={load} cmsLive={status?.cms_live} />)}</div>
      )}

      <NewsletterLoop />
    </div>
  );
};

const NewsletterLoop = () => {
  const [summary, setSummary] = useState(null);
  const [subs, setSubs] = useState([]);
  const [issues, setIssues] = useState([]);
  const [email, setEmail] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [showSubs, setShowSubs] = useState(false);

  const load = async () => {
    const [s, sub, iss] = await Promise.all([api.nlSummary(), api.nlSubscribers(), api.nlIssues()]);
    setSummary(s); setSubs(sub); setIssues(iss);
  };
  useEffect(() => { load(); }, []);

  const subscribe = async () => {
    const e = email.trim();
    if (!e || !e.includes("@")) { toast.error("Enter a valid email."); return; }
    try { await api.nlSubscribe(e); setEmail(""); await load(); toast.success("Reader subscribed."); }
    catch { toast.error("Couldn't subscribe that reader."); }
  };

  const draft = async () => {
    setDrafting(true);
    try {
      await api.nlDraft();
      toast.message("KURT is drafting this week's issue…");
      let n = 0;
      const t = setInterval(async () => { n++; await load(); if (n > 15) { clearInterval(t); setDrafting(false); } }, 4000);
    } catch { toast.error("Draft failed."); setDrafting(false); }
  };

  return (
    <div data-testid="newsletter-loop" className="mt-10">
      <h2 className="kz-head text-lg font-bold mb-1 flex items-center gap-2"><Mail size={16} className="text-emerald-400" /> Newsletter loop</h2>
      <p className="text-slate-400 text-sm mb-4">Capture readers and send a weekly issue — turn one-time readers into repeat readers on the way to members. {summary && !summary.delivery_live && <span className="text-amber-400">Mailbox not connected yet.</span>}</p>

      {summary && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <MiniG label="Active subscribers" value={summary.active_subscribers?.toLocaleString()} />
          <MiniG label="Total captured" value={summary.total_subscribers?.toLocaleString()} />
          <MiniG label="Issues sent" value={summary.issues_sent} />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input data-testid="nl-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && subscribe()} placeholder="reader@email.com — capture a reader"
          className="flex-1 bg-[#121824] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500/40" />
        <button data-testid="nl-subscribe-btn" onClick={subscribe} disabled={!email.includes("@")}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.06] text-slate-100 font-semibold text-sm hover:bg-white/[0.1] disabled:opacity-50 transition-all"><Plus size={15} className="text-emerald-400" /> Add reader</button>
        <button data-testid="nl-draft-btn" onClick={draft} disabled={drafting}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-[#04140d] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60 transition-all">
          {drafting ? <Loader2 size={16} className="animate-spin" /> : <PenLine size={16} />} Draft this week's issue
        </button>
      </div>

      {subs.length > 0 && (
        <div className="mb-4">
          <button data-testid="nl-toggle-subs" onClick={() => setShowSubs(!showSubs)} className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1">
            {showSubs ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {subs.length} subscribers
          </button>
          {showSubs && (
            <div className="mt-2 space-y-1">
              {subs.map((s) => (
                <div key={s.id} data-testid={`sub-${s.id}`} className="flex items-center justify-between text-xs bg-[#121824] border border-white/10 rounded-lg px-3 py-2">
                  <span className="text-slate-300 truncate">{s.email}</span>
                  <button data-testid={`sub-del-${s.id}`} onClick={async () => { await api.nlDeleteSubscriber(s.id); await load(); }} className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {issues.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#121824] p-6 text-center text-sm text-slate-400">No issues yet — let KURT draft this week's edition.</div>
      ) : (
        <div className="space-y-2">{issues.map((iss) => <IssueRow key={iss.id} iss={iss} onChange={load} deliveryLive={summary?.delivery_live} />)}</div>
      )}
    </div>
  );
};

const IssueRow = ({ iss, onChange, deliveryLive }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.nlSendIssue(iss.id);
      await onChange();
      if (r?.status === "held") toast.message(r.send_note || "Held — unlock sending to deliver.");
      else if (r?.status === "sending" || r?.status === "sent") toast.success("Sending to subscribers…");
      else toast.message("Queued.");
    } finally { setBusy(false); }
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.nlTestIssue(iss.id, testEmail.trim());
      toast.success(`Test sent to ${r.to} — check your inbox.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the test.");
    } finally { setTesting(false); }
  };
  const remove = async () => { setBusy(true); try { await api.nlDeleteIssue(iss.id); await onChange(); } finally { setBusy(false); } };
  const badge = { draft: "bg-slate-500/15 text-slate-300", queued: "bg-amber-500/15 text-amber-300", sent: "bg-emerald-500/15 text-emerald-300", failed: "bg-red-500/15 text-red-300" }[iss.status] || "bg-slate-500/15 text-slate-300";

  return (
    <div data-testid={`issue-${iss.id}`} className="rounded-xl border border-white/10 bg-[#121824] p-3">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-100 truncate">{iss.drafting ? "Drafting this week's issue…" : iss.subject}</span>
            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${badge}`}>{iss.status}</span>
            {iss.recipients != null && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400">{iss.recipients} readers</span>}
          </div>
          {iss.preview && <p className="text-xs text-slate-500 mt-0.5 truncate">{iss.preview}</p>}
        </div>
        {iss.drafting ? <Loader2 size={15} className="animate-spin text-emerald-400" /> :
          <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-slate-200">{open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</button>}
      </div>
      {open && !iss.drafting && (
        <div className="mt-3 space-y-3 text-sm">
          {iss.intro && <p className="text-slate-300 leading-relaxed">{iss.intro}</p>}
          {Array.isArray(iss.sections) && iss.sections.map((s, i) => (
            <div key={i} className="rounded-lg bg-[#0B0F17] border border-white/[0.06] p-2.5">
              <div className="text-[13px] font-semibold text-slate-100">{s.heading}</div>
              <p className="text-xs text-slate-400 mt-0.5">{s.blurb}</p>
              {s.slug && <p className="text-[10px] text-emerald-400/80 mt-1">/{s.slug}</p>}
            </div>
          ))}
          {iss.one_thing && <div className="rounded-lg bg-emerald-500/[0.06] border border-emerald-500/20 p-2.5"><div className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 mb-1">One thing this week</div><p className="text-xs text-slate-300">{iss.one_thing}</p></div>}
          {iss.cta && <p className="text-xs text-emerald-400 font-semibold">{iss.cta}</p>}
          <div className="flex gap-1.5 items-center flex-wrap">
            <button data-testid={`issue-send-${iss.id}`} onClick={send} disabled={busy || iss.status === "sent"} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-emerald-500 text-[#04140d] hover:bg-emerald-400 disabled:opacity-50"><Send size={12} /> {deliveryLive ? "Send to subscribers" : "Queue send"}</button>
            <button data-testid={`issue-del-${iss.id}`} onClick={remove} disabled={busy} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-red-500/25 text-red-300 hover:bg-red-500/10 ml-auto"><Trash2 size={12} /></button>
          </div>
          <div className="flex gap-1.5 items-center pt-2 mt-1 border-t border-white/[0.06]">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Preview safely</span>
            <input data-testid={`issue-test-email-${iss.id}`} value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
              placeholder="your@email.co.uk (defaults to ben@)" className="flex-1 min-w-0 bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-1.5 text-[11px] text-slate-200 outline-none focus:border-emerald-500/40" />
            <button data-testid={`issue-test-${iss.id}`} onClick={sendTest} disabled={testing || !deliveryLive} title={!deliveryLive ? "Mailbox not connected" : "Send a real preview to yourself"}
              className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50 whitespace-nowrap">
              {testing ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Send test to myself
            </button>
          </div>
          {iss.send_note && <p className="text-[10px] text-amber-400/90">{iss.send_note}</p>}
        </div>
      )}
    </div>
  );
};


const Stat = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/10 bg-[#121824] p-4 kz-fade-up">
    <Icon size={15} className="text-slate-500" />
    <div className={`kz-head text-xl font-extrabold mt-2 ${accent}`}>{value}</div>
    <div className="text-[11px] text-slate-400 mt-0.5">{label}</div>
    {sub && <div className="text-[10px] mt-1 text-slate-500">{sub}</div>}
  </div>
);

const MiniG = ({ label, value }) => (
  <div className="rounded-xl bg-[#0B0F17] border border-white/[0.06] p-3">
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className="kz-head text-lg font-extrabold text-emerald-400 mt-0.5">{value}</div>
  </div>
);

const RepurposeBox = ({ label, text }) => (
  <div className="rounded-lg bg-[#0B0F17] border border-white/[0.06] p-2.5">
    <div className="flex items-center justify-between mb-1">
      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">{label}</span>
      <button onClick={() => { navigator.clipboard?.writeText(text); toast.success("Copied."); }} className="text-slate-500 hover:text-emerald-400"><Copy size={11} /></button>
    </div>
    <p className="text-[11px] text-slate-400 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">{text}</p>
  </div>
);

const ArticleRow = ({ a, onChange, cmsLive }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(a.title);
  const [meta, setMeta] = useState(a.meta_description || "");
  const [body, setBody] = useState(a.body || "");
  const [busy, setBusy] = useState(false);

  const save = async () => { setBusy(true); try { await api.seoEditArticle(a.id, { title, meta_description: meta, body }); await onChange(); toast.success("Saved."); } finally { setBusy(false); } };
  const publish = async () => { setBusy(true); try { await api.seoPublish(a.id); await onChange(); toast.success(cmsLive ? "Published." : "Prepared — connect CMS to publish live."); } finally { setBusy(false); } };
  const repurpose = async () => { setBusy(true); try { await api.seoRepurpose(a.id); await onChange(); toast.success("Repurposed into 4 channels."); } catch { toast.error("Repurpose failed."); } finally { setBusy(false); } };
  const remove = async () => { setBusy(true); try { await api.seoDeleteArticle(a.id); await onChange(); } finally { setBusy(false); } };
  const badge = { draft: "bg-slate-500/15 text-slate-300", ready_to_publish: "bg-amber-500/15 text-amber-300", published: "bg-emerald-500/15 text-emerald-300" }[a.status] || "bg-slate-500/15 text-slate-300";

  return (
    <div data-testid={`article-${a.id}`} className="rounded-xl border border-white/10 bg-[#121824] p-3">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-100 truncate">{a.title}</span>
            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${badge}`}>{(a.status || "draft").replace(/_/g, " ")}</span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 truncate">/{a.slug} · {a.target_keyword}</p>
        </div>
        <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-slate-200">{open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</button>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          <input data-testid={`art-title-${a.id}`} value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm font-semibold outline-none focus:border-emerald-500/40" />
          <input data-testid={`art-meta-${a.id}`} value={meta} onChange={(e) => setMeta(e.target.value)} placeholder="Meta description" className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-slate-400 outline-none focus:border-emerald-500/40" />
          <textarea data-testid={`art-body-${a.id}`} value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-2 text-sm text-slate-300 outline-none focus:border-emerald-500/40 resize-y leading-relaxed" />
          <div className="flex gap-1.5">
            <button data-testid={`art-save-${a.id}`} onClick={save} disabled={busy} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-white/[0.06] text-slate-200 hover:bg-white/[0.1]"><Save size={12} /> Save</button>
            <button data-testid={`art-publish-${a.id}`} onClick={publish} disabled={busy} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-emerald-500 text-[#04140d] hover:bg-emerald-400"><Send size={12} /> {cmsLive ? "Publish" : "Prepare publish"}</button>
            <button data-testid={`art-repurpose-${a.id}`} onClick={repurpose} disabled={busy} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-white/[0.06] text-slate-200 hover:bg-white/[0.1]"><Share2 size={12} className="text-emerald-400" /> Repurpose</button>
            <button data-testid={`art-del-${a.id}`} onClick={remove} disabled={busy} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-red-500/25 text-red-300 hover:bg-red-500/10 ml-auto"><Trash2 size={12} /></button>
          </div>
          {a.publish_note && <p className="text-[10px] text-amber-400/90">{a.publish_note}</p>}
          {a.repurposed && (
            <div data-testid={`repurposed-${a.id}`} className="mt-2 grid sm:grid-cols-2 gap-2">
              {a.repurposed.linkedin && <RepurposeBox label="LinkedIn" text={a.repurposed.linkedin} />}
              {a.repurposed.instagram && <RepurposeBox label="Instagram" text={a.repurposed.instagram} />}
              {Array.isArray(a.repurposed.x_thread) && <RepurposeBox label="X thread" text={a.repurposed.x_thread.join("\n\n")} />}
              {a.repurposed.newsletter && <RepurposeBox label="Newsletter" text={a.repurposed.newsletter} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
